<template>
  <div class="container py-1 weather-statistics-editor">


    <div v-if="!allowStatistics">
      <div class="alert alert-warning" role="alert">
        <h4 class="alert-heading">Statistics are not enabled</h4>
        <p>
          Weather Statistics are only available to monthly or annual plan users during alpha testing. 
          Free and Metered accounts do not have access. Please <a href="/support/" class="text-primary text-decoration-underline">upgrade</a> to use this feature.
        </p>
      </div>
    </div>
    <!--
    <div v-else class="alert alert-primary my-3" role="alert">
      We are currently in the alpha testing phase, and this feature is available without additional cost to you during this period.  
      <br/><br/>
      Please report any issues that you encounter to our <a href="/support/" class="text-primary text-decoration-underline">Support Team</a>, and we appreciate your <a href="/support/" class="text-primary text-decoration-underline">feedback</a> as we finalize this functionality.
    </div>
    -->


     <!-- Step 1: Goal selection -->
    <!--<h1>Select a Statistics Type</h1>-->
       <!-- Data Summary Section -->
      <div class="alert alert-info mb-4" role="alert">
       <!-- <h6 class="alert-heading mb-2">
          <i class="bi bi-info-circle me-2"></i>Source Data Summary
        </h6>-->
        <div class="row">
          <div class="col-12 col-md-3">
            <strong>Locations:</strong>
            <span v-if="locationCount === 0" class="text-muted">No locations selected</span>
            <span v-else-if="locationCount === 1">{{ firstLocationText }}</span>
            <span v-else>{{ firstLocationText }} + {{ locationCount - 1 }} more</span>
          </div>
          <div class="col-12 col-md-9">
            <strong>Date Range:</strong>
            <span v-if="dateRangeText" class="text-nowrap">{{ dateRangeText }}</span>
            <span v-else class="text-muted">No date range specified</span>
            <div v-if="dateRangeCommentary" class="form-text text-muted mt-1">
              <i class="bi bi-info-circle me-1"></i> {{ dateRangeCommentary }} 
            </div>
          </div>
        </div>
      </div>
    <div v-if="!queryDefinition.goal || queryDefinition.goal === 'timeline'">
   

      <h2 class="mb-4">Select a Statistics Type</h2>
      <p class="lead">Choose the type of weather statistics you want to analyze.</p>



      <div class="row justify-content-center g-4" :class="{ 'opacity-25 pe-none': !allowStatistics }">
        <div class="col-md-3">
          <div class="card h-100 shadow-sm" @click="selectGoal('statisticalsummary')" role="button">
            <div class="card-body">
              <h5 class="card-title">📊 Statistical Summary</h5>
              <p class="card-text">
                Calculate aggregates such as maximum temperature or total precipitation over time periods like months or years.
              </p>
            </div>
          </div>
        </div>

        <div class="col-md-3">
          <div class="card h-100 shadow-sm" @click="selectGoal('frostdates')" role="button">
            <div class="card-body">
              <h5 class="card-title">❄️ Frost Dates</h5>
              <p class="card-text">
                Identify the first and last frost based on temperature thresholds, helpful for agriculture and planting.
              </p>
            </div>
          </div>
        </div>

        <div class="col-md-3 pe-none">
          <div class="card h-100 shadow-smposition-relative" @click="selectGoal('regionstats')" role="button" disabled="true">
            <span class="position-absolute top-0 end-0 badge bg-warning text-dark px-3 py-2 rounded-0 rounded-bottom-start">
              Coming Soon
            </span>
            <div class="card-body ">
              <h5 class="card-title">🔍 Filtered Dataset</h5>
              <p class="card-text  opacity-25">
                Filter large datasets by weather conditions like temperature or precipitation to identify values below or above thresholds.
              </p>
            </div>
          </div>
        </div>
        
        <div class="col-md-3 pe-none">
          <div class="card h-100 shadow-sm position-relative" @click="selectGoal('regionstats')" role="button" disabled="true">
            <span class="position-absolute top-0 end-0 badge bg-warning text-dark px-3 py-2 rounded-0 rounded-bottom-start">
              Coming Soon
            </span>
            <div class="card-body">
              <h5 class="card-title">🛣️ Route-based weather</h5>
              <p class="card-text opacity-25">
                Analyze weather conditions along travel routes and corridors for transportation planning and logistics optimization.
              </p>
            </div>
          </div>
        </div>
      </div>
      <!--
      <div class="text-center mt-5">
        <button class="btn btn-secondary" @click="clearGoal">
          Reset to Timeline (no statistics)
        </button>
      </div>
      -->
    </div>

    <!-- Step 2: Editor UI for selected goal -->
    <div v-else-if="step==='createdefinition'">


      <component
        :is="currentEditor"
        :definition="queryDefinition"
        @update="updateStatsDefinition"
      />
      <div class="text-end mt-3">
          <button v-if="isAdmin" class="btn btn-secondary me-2" @click="toggleRawJson">
            {{ showRawJson ? 'Hide' : 'Show' }} Raw JSON
          </button>
          <button class="btn btn-secondary" @click="clearGoal">
            ⬅️ Back to Goal Selection
          </button>
          <button class="btn btn-primary" @click="setStep('choosename')" :disabled="!hasValidDefn">
            Continue
          </button>
        </div>

        <div v-if="showRawJson" class="mt-4">
          <h6>Raw JSON:</h6>
          <pre class="bg-light p-3 border rounded small text-break">{{ formattedParams }}</pre>
        </div>
    </div>

    
    <template v-else-if="step==='choosename'">
      
      <div class="input-group align-items-center  mt-3">
        <a href="#" @click="modifyMetadata()">{{currentName}}</a>
        <button type="button" class="btn text-primary btn-sm p-1 fs-6"  @click="modifyMetadata()"><i class="bi bi-pencil"></i></button>
      </div>
      <div class="form-text mb-3">
        The name will be used in the <a href="#" class="text-primary text-decoration-underline" @click="openMyDatasets()">My Datasets</a> and for the file names.
      </div>
      <button type="button" class="d-block btn btn-primary btn-sm  mt-3"  @click="step='showsubmit';hasChosenName=true;" >
          Continue
      </button>
    </template>

    <template v-if="step==='showsubmit'">
      <div >
        {{currentName}}
      </div>


      <div v-if="showProcessing" class="mt-3" >
        <div class="d-flex align-items-center">
          <div class="spinner-border mx-2 me-3 " role="status" aria-hidden="true"></div>
          <strong>{{processingStatusText}}</strong>
        </div>
        <div v-if="queueText" class="form-label">
          {{queueText}}
        </div>
      </div>
      
      <div v-else-if="showResultError"  class="mt-3">
        <div class="alert alert-primary mt-3">
          <div>Your job did not complete successfully</div>
          <div>{{info}}</div>
        </div>
      </div>
      <div v-else-if="showResult"  class="mt-3">
        
          <div class="alert alert-primary mt-3">
            <div>Your job has completed successfully. Please download the dataset from <a href="#" class="text-primary text-decoration-underline" @click="openMyDatasets()">My Datasets</a> </div>
            <div>{{info}}</div>
          </div>
   
      </div>
      <div v-else class="mt-3 d-flex align-items-center justify-content-start">
        <div>
          <button type="button" class="btn btn-primary btn-sm"  @click="submit(true)"  >
            <span class="">Run Query</span>
          </button>
          
          <div class="form-text">
            Results are available for at least 24 hours. Click on <a href="#" class="text-primary text-decoration-underline" @click="openMyDatasets()">My Datasets</a> to access your recent datasets.
          </div>
        </div>
      </div>

      
    </template>

    <div class="modal fade" id="saveConfirmModal" tabindex="-1" aria-labelledby="saveConfirmModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-dialog-centered" modal-xl>
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="saveConfirmModalLabel">Data data definition saved</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form>
            <div class="modal-body">
                
              <div class="d-flex align-items-center justify-content-center">
                <strong>Your data definition has been saved successfully.</strong>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>


    <MetadataInfoComponent :metadata="metadata" :show="showMetadataInfo" @cancel="onMetadataInfoSubmit=null" @submit:metadata="setMetadataInfo"></MetadataInfoComponent>
    
  </div>
</template>

<script>
import StatisticalSummaryEditor from './weatherstatistics/StatisticalSummaryEditor.vue'
import FrostDatesEditor from './weatherstatistics/FrostDatesEditor.vue'
import RegionStatsEditor from './weatherstatistics/RegionStatsEditor.vue'
import MetadataInfoComponent from "/src/components/MetadataInfo.vue";
import {VcMetadataUtils} from "/src/visualcrossing-core/vcmetadatautils";
import {VcJobStatus} from  '/src/visualcrossing-core/vcjobstatus';
import { isValidDefn } from './weatherstatistics/weatherstatisticsutils.js';
import {VcDateUtils} from '/src/visualcrossing-core/vcdateutils'; 

export default {
  name: 'WeatherStatisticsEditor',
  components: {
    StatisticalSummaryEditor,
    FrostDatesEditor,
    RegionStatsEditor,
    MetadataInfoComponent
  },
  props: {
    sessionId:String,
    sessionState:Object,
    packageDefn: Object, 
    value: {
      type: Object,
      required: true
    }
  },
  emits: ['change:metadata', 'openmydatasets', 'exportcomplete','opensignup'],
  data() {
    return {
      localPackageDefn: this.createLocalPackageDefn(),
      showRawJson: false,
      showMetadataInfo:false,
      step:'createdefinition',
      metadata:this.metadataProp || {
        id:null,
        name:null,
        desc:null
      },
      existingJobPackages:[],
      hasSubmitted:false,
      hasProcessed:false,
      hasDownload:false,
      taskInstance:null,
      isRefreshingResults:false,
      showSubmitImportant:false,
      existingJobPackages:[],
      latestResult:null,
    }
  },
  computed: {
    subscriptionParams() {
      return this.sessionState && this.sessionState.subscriptionParams;
    },
    allowStatistics:function() {
      return this.subscriptionParams && this.subscriptionParams.allowStatistics;
    },
    isAdmin() {
      let me=this;
      return me.sessionState && me.sessionState.contextProperties &&  me.sessionState.contextProperties.isAdmin==="true";
    },
    hasValidDefn() {
      return this.queryDefinition && isValidDefn(this.queryDefinition);
    },
    formattedParams() {
      try {
        return JSON.stringify(this.localPackageDefn, null, 2)
      } catch (e) {
        return '// Error formatting JSON'
      }
    },
    currentEditor() {
      switch (this.queryDefinition.goal) {
        case 'statisticalsummary': return 'StatisticalSummaryEditor'
        case 'frostdates': return 'FrostDatesEditor'
        case 'regionstats': return 'RegionStatsEditor'
        default: {
          console.error('Unknown goal:', this.queryDefinition.goal)
          return null
        }
      }
    },
    queryDefinition() {
      return this.localPackageDefn && this.localPackageDefn.params;
    },

    statsDefinition() {
      try {
        return this.queryDefinition.statsdefinition ? JSON.parse(this.queryDefinition.statsdefinition) : {}
      } catch (e) {
        return {}
      }
    },
    currentName() {
      return (this.metadata && this.metadata.name) || this.defaultName;
    },
    defaultName() {
      var me=this;
      if (!me.queryDefinition) return "Undefined";
     
      return me.queryDefinition.goal;
    },
        showSubmit() {
      var me=this;
      return (me.showSubmitImportant || (!me.showProcessing && !me.showResult ));
    },
    showProcessing() {
      var me=this;
      return me.isRefreshingResults;
    },

    failed() {
      var me=this;
      if (!me.taskInstance) return 0;
      return me.taskInstance.failed;
    },
    successful() {
      var me=this;
      if (!me.taskInstance) return 0;
      return me.taskInstance.successful;
    },
    total() {
      var me=this;
      if (!me.taskInstance) return 0;
      return me.taskInstance.total;
    },
    completed() {
      var me=this;
      if (!me.taskInstance) return 0;
      return me.taskInstance.completed;
    },
    info() {
      var me=this;
      return me.taskInstance && me.taskInstance.info;
    },
    processingStatusText() {
      var me=this;
      if (!me.taskInstance) return "Submitting...";
      if (me.taskInstance.status===1) return "Queued";
      if (me.taskInstance.status===2) {
        if (me.taskInstance.completed>1 && me.taskInstance.total>1) {
          return `Running ${me.taskInstance.completed} of ${me.taskInstance.total}`;
        } else {
          return "Running";
        }
      }
      return null;
    },
    queueText() {
      var me=this;
      if (!me.taskInstance || me.taskInstance.status>1) return null;
      if (me.taskInstance.queuedJobs) return `${me.taskInstance.queuedJobs} jobs in queue`;
      return null;
    },
    showResult() {
      var me=this;
      //return !me.showSubmitImportant && me.taskResult;
      return !me.showSubmitImportant && me.taskInstance && me.taskInstance.status===3;
    },
    showResultError() {
      var me=this;
      return me.taskInstance && me.taskInstance.status===4;
    },

    locationCount() {
      return (this.localPackageDefn && this.localPackageDefn.locations && this.localPackageDefn.locations.length) || 0;
    },
    firstLocationText() {
      if (!this.localPackageDefn || !this.localPackageDefn.locations || this.localPackageDefn.locations.length === 0) {
        return '';
      }
      const firstLocation = this.localPackageDefn.locations[0];
      // Assuming locations have a 'name' or 'address' property, adjust as needed
      return firstLocation.name || firstLocation.address || firstLocation.location || 'Unknown location';
    },
    
    dateRange() {
      if (!this.localPackageDefn || !this.localPackageDefn.params) {
        return null;
      }
      const params = this.localPackageDefn.params;
      const startDate = params.startdate;
      const endDate = params.enddate;
      return VcDateUtils.resolveDateRange(startDate, endDate);
    },
    dateRangeText() {
      if (!this.dateRange) {
        return '-';
      }
      const [ startDate, endDate ] = this.dateRange;
      return VcDateUtils.resolveDateRangeText(startDate, endDate);
    },
     dateRangeCommentary() {
        if (!this.dateRange) {
        return 'Enter a date range to create a statistical summary.';
      }
      const [ startDate, endDate ] = this.dateRange;
      if (!startDate && !endDate) {
        return 'Verify your date range.';
      }
      
      // Check if date range is less than 365 days
      if (startDate && endDate) {
        const timeDifference = endDate.getTime() - startDate.getTime();
        const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
        
        if (daysDifference < 365) {
          return `Warning: Your date range is only ${daysDifference} days. Weather statistics work best with longer periods (1+ years).`;
        }
      }
      
      return null;
    }
  },
  methods: {
    openMyDatasets() {
      this.$emit("openmydatasets");
    },
    setStep(step) {
      this.step = step
    },
    createLocalPackageDefn() {
      var me=this;
      let newPackageDefn= me.packageDefn && JSON.parse(JSON.stringify(me.packageDefn));
      newPackageDefn.statsdefinition = "";
      return newPackageDefn;
    },
    toggleRawJson() {
      this.showRawJson = !this.showRawJson
    },
    selectGoal(goal) {
      this.queryDefinition.goal = goal
      this.queryDefinition.statsdefinition = ''
      //this.$emit('input', this.queryDefinition)
    },
    clearGoal() {
      this.queryDefinition.goal = 'timeline'
      this.queryDefinition.statsdefinition = ''
      //this.$emit('input', this.queryDefinition)
    },
    updateStatsDefinition(newDefinition) {
      this.queryDefinition.statsdefinition = JSON.stringify(newDefinition)
      //this.$emit('input', this.queryDefinition)
    },
    modifyMetadata() {
      var me=this;

      if (!me.metadata.name) me.metadata.name=me.defaultName;
      me.showMetadataInfo=true;
    },
    setMetadataInfo(metadata) {
      var me=this;
      me.showMetadataInfo=false;
      if (me.metadata && me.metadata.id) {
          window.VcCommonModals.showConfirm("Change existing object?", `Would you like create create a new definition or rename the exiting definition?'`, "Rename existing", "Create new")
            .then(function(id) {
              me.metadata=metadata;
              vcCommunicator.saveMetadata(me.metadata.id,me.metadata.type,me.metadata.name,me.metadata.desc, null);

            }).catch((error) => {
              me.metadata={
                id:null,
                name:metadata.name,
                desc:metadata.desc
              }
            });
          return;

      }
      me.metadata=metadata;
     
     // if (this.onMetadataInfoSubmit) this.onMetadataInfoSubmit();
    },
    submit(runOnce, silent, loadedExistingDefns) {
      var me=this;

     

      me.metadata.name= me.metadata.name || me.defaultName;
     
      me.showSubmitImportant=false;
      me.localPackageDefn.enabled=true;
      me.localPackageDefn.runOnce=true;

    

      if (!loadedExistingDefns) {

        vcCommunicator.searchMetadata(null,"JobPackage") //
          .then(function (results) {
            me.existingJobPackages=results && results.result;
            me.submit(runOnce, silent, true);
          })
          .catch((error) => {
            console.log("Failed refreshExistingPackageDefns:"+error);
             me.submit(runOnce, silent, true);
          });

        return;
      }


      if (!me.metadata.id) {
        var existingObject=me.getExistingPackageByName(me.metadata.name);
        if (existingObject) {
          window.VcCommonModals.showConfirm("Confirm overwrite", `There is an existing defintion with name '${me.metadata.name}'. Would you like to overwrite it?`)
            .then(function(id) {
              me.metadata=existingObject;
              me.submit(runOnce, silent);
            }).catch((error) => {
              console.log("User said no to delete")
            });
          return;

        }
      }

      me.hasSubmitted=true;


      var waitForResult=runOnce;
      
      me.latestResult=null;
      //me.taskResult=null;
      vcCommunicator.saveBulkSourceDefn(me.metadata.id,me.metadata.name,me.metadata.desc,me.localPackageDefn).then(function (result) {
        if (result && result.metadata) {
          me.metadata=result.metadata;
          
          if (!runOnce) {
            me.hasProcessed=true;
            me.hasDownload=true;
            me.$emit("exportcomplete");
          }

          //AMW - people seem confused when we set the overall session metadata in this flow
          //me.$emit("change:metadata", me.metadata);

          if (!waitForResult) {
            var target=me.$el && me.$el.querySelector("#saveConfirmModal");
            var modal = bootstrap.Modal.getOrCreateInstance(target);
            modal.show();
            return;
          }

          if (waitForResult && result.results) {
            result.results.forEach(result => {
              if (VcMetadataUtils.getSafeResultTime(me.latestResult)<VcMetadataUtils.getSafeResultTime(result)) {
                me.latestResult=result;
              }
            });
           
          } 

          me.isRefreshingResults=true;
          me.delayedRefreshPackageResults();
        }


      }).catch((error) => {
        console.log("Failed saveBulkSourceDefn:"+error);
        
        window.VcCommonModals.showError("Error saving export information", "There was a problem exporting the data definition. <br/>"+error);
        
      });
    },
    getExistingPackageByName(name) {
      var me=this;
      if (!me.existingJobPackages) return null;
      name=(name && name.toLowerCase());
      return me.existingJobPackages.reduce(function(a,d) {
        return a || (d && d.name && d.name.toLowerCase() === name && d);
      }, null);
    },
    delayedRefreshPackageResults() {
      var me=this;
      setTimeout(function() {
            me.refreshPackageResults()
          }, 2500);
    },
    refreshPackageResults() {
      var me=this;
      if (!me.metadata || !me.metadata.id) {
        console.log("No md for refreshPackageResults");
        return;
      }
      vcCommunicator.resultsByJobPackage(me.metadata.id)
        .then(function (packageResults) {
          if (packageResults && packageResults[0] && packageResults[0].instance) {
            me.taskInstance=packageResults[0].instance;

            if (packageResults[0].runonce) {
              me.taskInstance.status=VcJobStatus.QUEUED;
            }

          } else {
            me.taskInstance=null;
          }


          me.isRefreshingResults=me.isRefreshingResults && !me.taskInstance || !VcJobStatus.isFinishedState(me.taskInstance.status);

          
          if (me.isRefreshingResults) {
            me.delayedRefreshPackageResults();
          } else {
            me.hasProcessed=true;
            me.$emit("exportcomplete");
          }
        }).catch((error) => {
          console.log("Failed resultsByJobPackage:"+error);
          me.isRefreshingResults=me.isRefreshingResults && !me.taskInstance || !VcJobStatus.isFinishedState(me.taskInstance.status);
          if (me.isRefreshingResults) {
            me.delayedRefreshPackageResults();
          }
        });
    },
  },
  watch: {
    /*
    value: {
      handler(newVal) {
        this.params = { ...newVal }
      },
      deep: true
    }*/
  },
 
}
</script>

<style scoped>
.card {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.card:hover {
  transform: scale(1.03);
  box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.1);
}
.coming-soon-ribbon {
  position: absolute;
  top: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.8);
  color: #333;
  padding: 0.5rem;
  font-size: 0.9rem;
  font-weight: bold;
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
